
import { ManualVal } from "@/types/common"
import { isNumber, isUndefined } from "lodash-es";
import { useContext, useMemo, useState } from "react"
import { NodeState } from "@/components/FlowSheetEditor/services/modal"
import { PropertySetModule } from "@/services/admin/property-set/modal"
import { useUpdateEffect } from "ahooks"
import { Button } from "antd"
import { UndoOutlined } from "@ant-design/icons"
import { useGetVariableUnit } from "@/pages/Project/hooks/useDimUnits"
import { ManualCell } from "../ManualCell"
import { variableLabel } from "../utils"
import { getProjectPropertySets } from "@/store/project/selector"
import { useSelector } from "react-redux"

interface ComponentSplitterRecord {
  name: string
  topOut?: ManualVal
  bottomOut?: ManualVal
  componentIndex: number
  split?: ManualVal
}

interface CellChange {
  val: number | undefined
  key: string
  isDelete?: boolean
}

export interface CellChanges {
  [key: string]: CellChange
}

interface CompositionSplitProps {
  data: any
  unit: NodeState
  onChange?: (value: CellChanges) => void
  readonly?: boolean
}

export function CompositionSplit(props: CompositionSplitProps) {
  const {data: simData, onChange, readonly = true} = props
  const propertySets = useSelector(getProjectPropertySets)
  const setName = simData?.args?.tpps
  const [data, setData] = useState(toSplitData(propertySets, setName, simData, readonly))

  const getVariableUnit = useGetVariableUnit()

  useUpdateEffect(() => {
    setData(toSplitData(propertySets, setName, simData, readonly))
  }, [simData, propertySets, setName, readonly])

  const updateTopOut = (val: number | undefined, cellData: ComponentSplitterRecord) => {
    const changeKey = `topout_${cellData.componentIndex}`
    const change = {val, key: `:TOP_OUT:xi_$1[${cellData.componentIndex}]`}
    if (isUndefined(val)) {
      data[cellData.componentIndex].topOut = undefined
    }
    setData([...data])
    onChange?.({[changeKey]: change})
  }

  const updateBottomOut = (val: number | undefined, cellData: ComponentSplitterRecord) => {
    const key = `bottomout_${cellData.componentIndex}`
    const change = {val, key: `:BOTTOM_OUT:xi_$1[${cellData.componentIndex}]`}
    if (isUndefined(val)) {
      data[cellData.componentIndex].bottomOut = undefined
    }
    setData([...data])
    onChange?.({[key]: change})
  }

  const updateSplit = (val: number | undefined, cellData: ComponentSplitterRecord) => {
    const key = `split_${cellData.componentIndex}`
    const change = {val, key: `:UNIT:SPLIT_$1[${cellData.componentIndex}]`}
    if (isUndefined(val)) {
      data[cellData.componentIndex].split = undefined
    }
    setData([...data])
    onChange?.({[key]: change})
  }

  const removeNumber = (val: ManualVal) => {
    if(isNumber(val)) {
      return undefined
    }
    return val
  }

  const reset = () => {
    const newData = data.map(item => {
      return {
        ...item,
        topOut: removeNumber(item.topOut),
        bottomOut: removeNumber(item.bottomOut),
        split:  removeNumber(item.split),
      }
    })
    setData(newData)
  }

  const unitEntiries = useMemo(() => {
    const  molFrac = getVariableUnit('MOL_FRAC');
    const  pf = getVariableUnit('PF');
    return {
      topOut: molFrac,
      bottomOut:molFrac,
      split: pf
    }
  }, [getVariableUnit])

  return <div style={{overflowX: 'auto'}}>
    {!readonly && <div className="flex-end mb-1"><Button onClick={reset} icon={<UndoOutlined />} title="Reset"/></div>}
    <table style={{width: '100%'}} className="bordered-table break-word-table">
    <thead>
      <tr>
        <th rowSpan={2} scope="col" style={{width: 200}}>Component</th>
        <th colSpan={2}>Compositions</th>
        <th rowSpan={2} scope="col" style={{width: 200}}>{variableLabel('split ratios', unitEntiries.split)}</th>
      </tr>
      <tr>
        <th scope="col" style={{width: 200}}>{variableLabel('Top Out', unitEntiries.topOut)}</th>
        <th scope="col" style={{width: 200}}>{variableLabel('Bottom Out', unitEntiries.bottomOut)}</th>
      </tr>
      </thead>
      <tbody>
        {data.map((item, index) => {
        return <tr key={item.name}>
          <td>{item.name}</td>
          <td>
            <ManualCell cellData={item.topOut}
              onChange={(val) => updateTopOut(val, item)}
              onDelete={() => updateTopOut(undefined, item)}
              dimCode="MOL_FRAC"
              unitEntiry={unitEntiries.topOut}
            />
          </td>
          <td>
            <ManualCell cellData={item.bottomOut}
              onChange={(val) => updateBottomOut(val, item)}
              onDelete={() => updateBottomOut(undefined, item)}
              dimCode="MOL_FRAC"
              unitEntiry={unitEntiries.bottomOut}
            />
          </td>
          <td>
            <ManualCell cellData={item.split}
              onChange={(val) => updateSplit(val, item)}
              onDelete={() => updateSplit(undefined, item)}
              dimCode="PF"
              unitEntiry={unitEntiries.split}
            />
          </td>
        </tr>
        })}
      </tbody>
    </table>
  </div>
  
}

function toSplitData(propertySets: PropertySetModule.Modal[], setName: string, simData: any, readonly: boolean) {
  if (!(setName && propertySets)) {
      return []
    }
    const propertySet = propertySets.find(item => item.name === setName)
    if (!propertySet) {
      return []
    }
    const topOut = simData?.vars?.TOP_OUT || {};
    const bottomOut = simData?.vars?.BOTTOM_OUT || {};
    const unitData = simData?.vars?.UNIT || {};
    const inData = simData?.vars?.IN || {};
    let dataList = propertySet.components.map((item, index) => {
      const componentInTopOut = topOut?.['xi_$1']?.[index];
      const componentInBottomOut = bottomOut?.['xi_$1']?.[index];
      const split = unitData?.SPLIT_$1?.[index]
      return {
        name: item.name,
        topOut: componentInTopOut,
        bottomOut: componentInBottomOut,
        split,
        componentIndex: index,
      }
    })

    if (readonly) {
      const validComponents = inData.xi_$1?.val ?? inData.xi_$1 ?? [];
      dataList = dataList.filter((item, index) => {
        return !!validComponents[index]
      })
    }
    return dataList;
}
